import { forwardRef, type ReactNode, useState } from 'react';
import type { HTMLAttributes } from 'react';
import { motion, type MotionProps, useMotionValue, useSpring } from 'framer-motion';
import { cn } from '@utils';

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, keyof MotionProps> {
  variant?: 'default' | 'gradient' | 'glass';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  hover?: boolean; // Enable hover animation
  tilt3d?: boolean; // Enable 3D tilt effect on hover
  whileHover?: MotionProps['whileHover']; // Custom hover animation
  whileTap?: MotionProps['whileTap']; // Custom tap animation
  children?: ReactNode;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({
    className,
    variant = 'default',
    padding = 'md',
    hover = false,
    tilt3d = false,
    whileHover,
    whileTap,
    children,
    ...props
  }, ref) => {
    const [isHovered, setIsHovered] = useState(false);
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const rotateX = useSpring(useMotionValue(0), { stiffness: 300, damping: 30 });
    const rotateY = useSpring(useMotionValue(0), { stiffness: 300, damping: 30 });

    const baseStyles = 'rounded-xl transition-all duration-200';

    const variants = {
      default: 'bg-purple-900/40 border border-purple-500/30',
      gradient: 'bg-gradient-to-br from-purple-900/50 to-indigo-900/50 border border-purple-500/30',
      glass: 'bg-white/5 backdrop-blur-md border border-white/10',
    };

    const paddings = {
      none: '',
      sm: 'p-3',
      md: 'p-4',
      lg: 'p-6',
    };

    // Default hover animations if hover is enabled
    const defaultHover = hover ? { scale: 1.02, y: -2 } : undefined;
    const defaultTap = hover ? { scale: 0.98 } : undefined;

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!tilt3d) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const mouseX = e.clientX - centerX;
      const mouseY = e.clientY - centerY;

      const rotateXValue = (mouseY / (rect.height / 2)) * -15;
      const rotateYValue = (mouseX / (rect.width / 2)) * 15;

      rotateX.set(rotateXValue);
      rotateY.set(rotateYValue);
      x.set(mouseX * 0.1);
      y.set(mouseY * 0.1);
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
      if (tilt3d) {
        rotateX.set(0);
        rotateY.set(0);
        x.set(0);
        y.set(0);
      }
    };

    const Component = hover || tilt3d ? motion.div : 'div';

    return (
      <Component
        ref={ref}
        className={cn(
          baseStyles,
          variants[variant],
          paddings[padding],
          'relative overflow-hidden',
          className
        )}
        style={
          tilt3d
            ? {
                transformStyle: 'preserve-3d',
                perspective: '1000px',
              }
            : undefined
        }
        whileHover={whileHover || defaultHover}
        whileTap={whileTap || defaultTap}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={handleMouseLeave}
        animate={
          tilt3d
            ? {
                rotateX: rotateX.get(),
                rotateY: rotateY.get(),
                x: x.get(),
                y: y.get(),
              }
            : undefined
        }
        transition={{
          type: 'spring',
          stiffness: 400,
          damping: 17,
        }}
        {...props}
      >
        {/* Glow effect on hover */}
        {(hover || tilt3d) && (
          <motion.div
            className="absolute inset-0 rounded-xl pointer-events-none"
            animate={
              isHovered
                ? {
                    boxShadow: [
                      '0 0 20px rgba(168, 85, 247, 0.3)',
                      '0 0 40px rgba(168, 85, 247, 0.6)',
                      '0 0 20px rgba(168, 85, 247, 0.3)',
                    ],
                  }
                : { boxShadow: '0 0 0px rgba(168, 85, 247, 0)' }
            }
            transition={{
              duration: 1.5,
              repeat: isHovered ? Infinity : 0,
              ease: 'easeInOut',
            }}
          />
        )}

        {/* Shine effect on hover for 3D cards */}
        {tilt3d && isHovered && (
          <motion.div
            className="absolute inset-0 rounded-xl pointer-events-none"
            style={{
              background:
                'radial-gradient(circle at var(--mouse-x) var(--mouse-y), rgba(255,255,255,0.15) 0%, transparent 50%)',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
        )}

        <div style={tilt3d ? { transform: 'translateZ(50px)' } : undefined}>{children}</div>
      </Component>
    );
  }
);

Card.displayName = 'Card';
